import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Medal, 
  Award, 
  Target, 
  TrendingUp, 
  Users,
  Crown
} from 'lucide-react';
import { useQuery } from 'react-query';
import axios from 'axios';

const LeaderboardContainer = styled.div`
  min-height: 100vh;
  background: var(--color-background);
`;

const Header = styled.section`
  background: var(--gradient-primary);
  color: white;
  padding: var(--spacing-2xl) 0;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-md);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Content = styled.section`
  padding: var(--spacing-2xl) 0;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-2xl);
  border-bottom: 1px solid var(--color-border);
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg) var(--spacing-xl);
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--color-text-secondary);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    color: var(--color-primary);
  }
  
  &.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const LeaderboardGrid = styled.div`
  display: grid;
  gap: var(--spacing-lg);
`;

const TopThree = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-2xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    order: 2;
  }
`;

const PodiumCard = styled(motion.div)`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  }
  
  &.first {
    order: 2;
    transform: scale(1.05);
    border-color: #ffd700;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #ffd700, #ffed4e);
    }
    
    @media (max-width: 768px) {
      order: 1;
      transform: none;
    }
  }
  
  &.second {
    order: 1;
    border-color: #c0c0c0;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #c0c0c0, #e5e5e5);
    }
    
    @media (max-width: 768px) {
      order: 2;
    }
  }
  
  &.third {
    order: 3;
    border-color: #cd7f32;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #cd7f32, #daa520);
    }
  }
`;

const RankBadge = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-md);
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  
  &.first {
    background: linear-gradient(135deg, #ffd700, #ffed4e);
  }
  
  &.second {
    background: linear-gradient(135deg, #c0c0c0, #e5e5e5);
  }
  
  &.third {
    background: linear-gradient(135deg, #cd7f32, #daa520);
  }
`;

const UserAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-md);
  color: white;
  font-size: 2rem;
  font-weight: 700;
`;

const UserName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
`;

const UserScore = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: var(--spacing-xs);
`;

const UserStats = styled.div`
  color: var(--color-text-muted);
  font-size: 0.875rem;
`;

const RegularList = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
`;

const ListHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr auto auto;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: var(--color-background);
  border-bottom: 1px solid var(--color-border);
  font-weight: 600;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ListItem = styled(motion.div)`
  display: grid;
  grid-template-columns: 60px 1fr auto auto;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--color-border);
  transition: background-color var(--animation-duration) ease;
  
  &:hover {
    background: var(--color-background);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const Rank = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: var(--color-text-primary);
  font-size: 1.125rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--gradient-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
  }
  
  .name {
    font-weight: 500;
    color: var(--color-text-primary);
  }
`;

const Score = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  color: var(--color-primary);
  font-size: 1.125rem;
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--color-text-muted);
  
  .icon {
    width: 64px;
    height: 64px;
    margin: 0 auto var(--spacing-lg);
    color: var(--color-text-muted);
    opacity: 0.5;
  }
  
  h3 {
    font-size: 1.25rem;
    margin-bottom: var(--spacing-sm);
    color: var(--color-text-secondary);
  }
`;

const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState('accuracy');

  const { data: leaderboardData, isLoading, error } = useQuery(
    ['leaderboard', activeTab],
    async () => {
      const apiType = activeTab === 'totalQuizzes' ? 'total_quizzes' : activeTab;
      const response = await axios.get(`/api/users/leaderboard?type=${apiType}`);
      return response.data.data;
    },
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const tabs = [
    { id: 'accuracy', label: '正确率排行', icon: Target },
    { id: 'totalQuizzes', label: '测验数量排行', icon: Trophy },
    { id: 'achievements', label: '成就排行', icon: Award }
  ];

  const getScoreDisplay = (user) => {
    switch (activeTab) {
      case 'accuracy':
        return `${user.stats?.accuracy || 0}%`;
      case 'totalQuizzes':
        return user.stats?.totalQuizzes || 0;
      case 'achievements':
        return user.stats?.achievements || 0;
      default:
        return '0';
    }
  };

  const getStatsDisplay = (user) => {
    switch (activeTab) {
      case 'accuracy':
        return `${user.stats?.totalQuizzes || 0} 次测验`;
      case 'totalQuizzes':
        return `${user.stats?.accuracy || 0}% 正确率`;
      case 'achievements':
        return `${user.stats?.totalQuizzes || 0} 次测验`;
      default:
        return '';
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown />;
      case 2:
        return <Medal />;
      case 3:
        return <Award />;
      default:
        return rank;
    }
  };

  if (isLoading) {
    return (
      <LeaderboardContainer>
        <Header>
          <HeaderContent>
            <Title>排行榜</Title>
            <Subtitle>查看学习高手们的优异表现，激励自己不断进步</Subtitle>
          </HeaderContent>
        </Header>
        <LoadingContainer>
          <LoadingSpinner />
          <p>正在加载排行榜...</p>
        </LoadingContainer>
      </LeaderboardContainer>
    );
  }

  if (error) {
    return (
      <LeaderboardContainer>
        <Header>
          <HeaderContent>
            <Title>排行榜</Title>
            <Subtitle>查看学习高手们的优异表现，激励自己不断进步</Subtitle>
          </HeaderContent>
        </Header>
        <Content>
          <ContentWrapper>
            <EmptyState>
              <TrendingUp className="icon" />
              <h3>加载失败</h3>
              <p>无法加载排行榜数据，请稍后重试</p>
            </EmptyState>
          </ContentWrapper>
        </Content>
      </LeaderboardContainer>
    );
  }

  const topThree = leaderboardData?.slice(0, 3) || [];
  const remaining = leaderboardData?.slice(3) || [];

  return (
    <LeaderboardContainer>
      <Header>
        <HeaderContent>
          <Title>排行榜</Title>
          <Subtitle>查看学习高手们的优异表现，激励自己不断进步</Subtitle>
        </HeaderContent>
      </Header>

      <Content>
        <ContentWrapper>
          <TabContainer>
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <Tab
                  key={tab.id}
                  className={activeTab === tab.id ? 'active' : ''}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <IconComponent />
                  {tab.label}
                </Tab>
              );
            })}
          </TabContainer>

          {leaderboardData && leaderboardData.length > 0 ? (
            <LeaderboardGrid>
              {topThree.length > 0 && (
                <TopThree>
                  {topThree.map((user, index) => {
                    const rank = index + 1;
                    const rankClass = rank === 1 ? 'first' : rank === 2 ? 'second' : 'third';
                    
                    return (
                      <PodiumCard
                        key={user._id}
                        className={rankClass}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <RankBadge className={rankClass}>
                          {getRankIcon(rank)}
                        </RankBadge>
                        <UserAvatar>
                          {user.username.charAt(0).toUpperCase()}
                        </UserAvatar>
                        <UserName>{user.username}</UserName>
                        <UserScore>{getScoreDisplay(user)}</UserScore>
                        <UserStats>{getStatsDisplay(user)}</UserStats>
                      </PodiumCard>
                    );
                  })}
                </TopThree>
              )}

              {remaining.length > 0 && (
                <RegularList>
                  <ListHeader>
                    <div>排名</div>
                    <div>用户</div>
                    <div>分数</div>
                    <div>统计</div>
                  </ListHeader>
                  {remaining.map((user, index) => {
                    const rank = index + 4;
                    return (
                      <ListItem
                        key={user._id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: (index + 3) * 0.05 }}
                      >
                        <Rank>#{rank}</Rank>
                        <UserInfo>
                          <div className="avatar">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <div className="name">{user.username}</div>
                        </UserInfo>
                        <Score>{getScoreDisplay(user)}</Score>
                        <Stats>{getStatsDisplay(user)}</Stats>
                      </ListItem>
                    );
                  })}
                </RegularList>
              )}
            </LeaderboardGrid>
          ) : (
            <EmptyState>
              <Users className="icon" />
              <h3>暂无排行数据</h3>
              <p>还没有用户参与测验，快来成为第一名吧！</p>
            </EmptyState>
          )}
        </ContentWrapper>
      </Content>
    </LeaderboardContainer>
  );
};

export default Leaderboard;
